import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "./ui/UIElements/Icon";
import { useLogout } from "@finitless/shared/hooks";
import { isWhiteLabel } from "@finitless/shared/lib";
import {
  useToastContext,
  useWebsocket,
  useBrand,
} from "@finitless/shared/contexts";
import { useUserStore, useThemeState } from "@finitless/shared/stores";
import { useOrderNotifications } from "../hooks/useOrderNotifications";
import {
  useBilling,
  useBillingRedirect,
  useIsProjectOwner,
} from "../hooks/useBilling";
import { useBillingStore } from "../stores/billingStore";
import {
  BusinessDropdown,
  LiveIndicator,
  LocaleSelector,
  BillingStatusIndicator,
} from "./ui";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../hooks/useUiLocale";
// ProductSwitcher removed - Connect consolidated into Dashboard

const SUPPORT_BASE_URL = "https://docs.finitless.com/doc/";

const LEARN_PAGE_MAP = {
  "/overview": { labelKey: "sidebar.overview", path: "quickstart" },
  "/ai-agents": {
    labelKey: "sidebar.aiAgents",
    path: "creating-a-finitless-ai-agent",
  },
  "/orders": { labelKey: "sidebar.orders", path: "ai-orders-dashboard" },
  "/users": { labelKey: "sidebar.customers", path: "users" },
  "/analyze/conversations": {
    labelKey: "sidebar.conversations",
    path: "conversations",
  },
  "/analyze/voice-calls": {
    labelKey: "sidebar.voiceCalls",
    path: "voice-calls",
  },
  "/analyze/reports": { labelKey: "sidebar.reports", path: "analyze-reports" },
  "/menu/stores": { labelKey: "sidebar.stores", path: "stores" },
  "/menu/products": { labelKey: "sidebar.products", path: "creating-a-menu" },
  "/menu/brands": { labelKey: "sidebar.brands", path: "brands" },
  "/growth/cart-recovery": {
    labelKey: "sidebar.cartRecovery",
    path: "cart-recovery",
  },
  "/campaigns": { labelKey: "sidebar.campaigns", path: "campaigns" },
  "/discounts": { labelKey: "sidebar.discounts", path: "discounts" },
  "/growth/link-in-bio": {
    labelKey: "sidebar.linkInBio",
    path: "link-in-bio",
  },
  "/integrations/pos": {
    labelKey: "sidebar.posSystems",
    path: "setup-pos-systems",
  },
  "/integrations/menu-importers": {
    labelKey: "sidebar.menuImporters",
    path: "menu-importers",
  },
  "/integrations/sales-channels": {
    labelKey: "sidebar.salesChannels",
    path: "setup-sales-channels",
  },
  "/integrations/delivery-services": {
    labelKey: "sidebar.deliveryServices",
    path: "setup-delivery",
  },
  "/delivery/drivers-managers": {
    labelKey: "sidebar.driversManagers",
    path: "delivery-driver-manager",
  },
  "/delivery/groups": {
    labelKey: "sidebar.deliveryGroups",
    path: "delivery-groups",
  },
  "/settings/basic": { labelKey: "sidebar.general", path: "settings" },
  "/settings/online-payments": {
    labelKey: "sidebar.onlinePayments",
    path: "online-payments",
  },
  "/settings/agent-translations": {
    labelKey: "sidebar.agentTranslations",
    path: "agent-translations",
  },
  "/settings/admins": { labelKey: "sidebar.admins", path: "admins" },
  "/support": {
    labelKey: "sidebar.support",
    path: "creating-a-support-ticket",
  },
};

const getCurrentPageInfo = (pathname) => {
  const sortedKeys = Object.keys(LEARN_PAGE_MAP).sort(
    (a, b) => b.length - a.length,
  );
  for (const key of sortedKeys) {
    if (pathname === key || pathname.startsWith(key + "/")) {
      return LEARN_PAGE_MAP[key];
    }
  }
  return null;
};

const TopBar = ({ onToggleSidebar, onToggleMobileSidebar }) => {
  const { t } = useTranslation(["app", "navigation", "toasts", "billing"]);
  const { locale, setLocale, supportedLocales } = useUiLocale();
  const location = useLocation();
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [isLearnDropdownOpen, setIsLearnDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userDropdownRef = useRef(null);
  const learnDropdownRef = useRef(null);
  const navigate = useNavigate();
  const currentPageInfo = getCurrentPageInfo(location.pathname);
  const logoutMutation = useLogout();
  const toast = useToastContext();
  const { user } = useUserStore();
  const { theme, setTheme } = useThemeState();
  const {
    isConnected,
    connectionState,
    reconnectAttempt,
    maxReconnectAttempts,
  } = useWebsocket();
  const { isEnabled: notificationsEnabled, toggleNotifications } =
    useOrderNotifications();

  const { selectedBrand, handleBrandChange, state } = useBrand();
  const { redirectToBillingPortal, isRedirecting } = useBillingRedirect();
  const { upgradePlanId } = useBilling();
  const setShowUpgradeModal = useBillingStore((s) => s.setShowUpgradeModal);

  const hideThemeToggle = false;

  const isProjectOwner = useIsProjectOwner();
  const showUpgradeToPro = isProjectOwner && !!upgradePlanId;

  // Show brand selector only on specific pages
  const shouldShowBrandSelector = false;
  const handleBrandSelect = (brand) => {
    handleBrandChange(brand);
    setIsBrandDropdownOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      toast.success(t("auth.logoutSuccess", { ns: "toasts" }));
      setIsUserDropdownOpen(false);
    } catch {
      toast.error(t("auth.logoutFailed", { ns: "toasts" }));
    }
  };

  const handleThemeToggle = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
  };

  const handleMyAccountClick = () => {
    navigate("/account");
    setIsUserDropdownOpen(false);
  };

  const handleLocaleChange = async (event) => {
    await setLocale(event.target.value);
  };

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsBrandDropdownOpen(false);
      }
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setIsUserDropdownOpen(false);
      }
      if (
        learnDropdownRef.current &&
        !learnDropdownRef.current.contains(event.target)
      ) {
        setIsLearnDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-sidebar border-border flex h-16 items-center justify-between border-b px-2 sm:px-4">
      {/* Left Side */}
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        {/* Mobile Menu Button */}
        <button
          onClick={onToggleMobileSidebar}
          className="hover:bg-accent flex-shrink-0 rounded-md p-1.5 transition-colors lg:hidden"
        >
          <Icon name="Menu" className="text-foreground h-5 w-5" />
        </button>

        {/* Brand Switcher - Only shown on overview, orders, and conversations */}
        {shouldShowBrandSelector && (
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={onToggleSidebar}
              className="hover:bg-accent hidden flex-shrink-0 rounded-md p-1 transition-colors lg:block"
            >
              <Icon name="Menu" className="text-muted-foreground h-4 w-4" />
            </button>

            <div className="relative min-w-0 flex-1" ref={dropdownRef}>
              <button
                onClick={() => {
                  if (state === "error" || state === "empty") return;
                  setIsBrandDropdownOpen(!isBrandDropdownOpen);
                }}
                disabled={
                  state === "loading" || state === "error" || state === "empty"
                }
                className={`flex w-full min-w-0 items-center gap-1 rounded-md px-2 py-1.5 transition-colors sm:gap-2 ${
                  isBrandDropdownOpen ? "bg-accent" : "hover:bg-accent"
                } ${
                  state === "loading" || state === "error" || state === "empty"
                    ? "cursor-not-allowed opacity-50"
                    : ""
                }`}
              >
                {state === "loading" ? (
                  <>
                    <div className="border-muted-foreground h-4 w-4 flex-shrink-0 animate-spin rounded-full border-b-2"></div>
                    <span className="text-muted-foreground truncate text-xs sm:text-sm">
                      {t("topbar.loading", { ns: "navigation" })}
                    </span>
                  </>
                ) : state === "error" ? (
                  <>
                    <Icon
                      name="AlertCircle"
                      className="h-4 w-4 flex-shrink-0 text-red-400"
                    />
                    <span className="truncate text-xs text-red-400 sm:text-sm">
                      {t("topbar.failed", { ns: "navigation" })}
                    </span>
                  </>
                ) : state === "empty" ? (
                  <>
                    <Icon
                      name="Building"
                      className="text-muted-foreground h-4 w-4 flex-shrink-0"
                    />
                    <span className="text-muted-foreground truncate text-xs sm:text-sm">
                      {t("topbar.noBusinesses", { ns: "navigation" })}
                    </span>
                  </>
                ) : selectedBrand ? (
                  <>
                    <span className="text-foreground max-w-32 truncate text-xs font-normal sm:text-sm lg:max-w-48">
                      {selectedBrand.name}
                    </span>
                    <Icon
                      name="ChevronDown"
                      className="text-muted-foreground h-4 w-4 flex-shrink-0"
                    />
                  </>
                ) : (
                  <>
                    <span className="text-muted-foreground truncate text-xs sm:text-sm">
                      {t("topbar.selectBusiness", { ns: "navigation" })}
                    </span>
                    <Icon
                      name="ChevronDown"
                      className="text-muted-foreground h-4 w-4 flex-shrink-0"
                    />
                  </>
                )}
              </button>

              <BusinessDropdown
                isOpen={isBrandDropdownOpen}
                onClose={() => setIsBrandDropdownOpen(false)}
                selectedBrand={selectedBrand}
                onBrandSelect={handleBrandSelect}
              />
            </div>

            {state === "success" && selectedBrand?.address && (
              <>
                <div className="text-muted-foreground hidden sm:block">|</div>
                {/* Address - Hidden on mobile to save space */}
                <span className="text-muted-foreground hidden max-w-32 truncate text-xs sm:text-sm lg:max-w-48 xl:block">
                  {selectedBrand.address}
                </span>
              </>
            )}
          </div>
        )}

        {/* Show only the sidebar toggle for pages without brand selector */}
        {!shouldShowBrandSelector && (
          <button
            onClick={onToggleSidebar}
            className="hover:bg-accent hidden flex-shrink-0 rounded-md p-1 transition-colors lg:block"
          >
            <Icon name="Menu" className="text-muted-foreground h-4 w-4" />
          </button>
        )}
      </div>

      {/* Right Side */}
      <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
        {/* Billing status indicator */}
        <div className="hidden sm:block">
          <BillingStatusIndicator />
        </div>

        {/* Upgrade to Pro - only when plan has upgrade_plan_id */}
        {showUpgradeToPro && (
          <button
            type="button"
            onClick={() => setShowUpgradeModal(true)}
            className="flex items-center gap-1 rounded-md bg-amber-600/20 px-2 py-1.5 transition-colors hover:bg-amber-600/30 sm:gap-2"
            aria-label={t("upgradeToPro", { ns: "billing" })}
          >
            <Icon
              name="Star"
              className="h-4 w-4 flex-shrink-0 text-amber-500"
            />
            <span className="hidden text-sm font-medium text-amber-400 sm:inline">
              {t("upgradeToPro", { ns: "billing" })}
            </span>
          </button>
        )}

        {/* Manage Billing - only for project owners */}
        {isProjectOwner && (
          <button
            type="button"
            onClick={() =>
              redirectToBillingPortal().catch((err) =>
                toast.error(
                  err?.message || t("redirectFailed", { ns: "billing" }),
                ),
              )
            }
            disabled={isRedirecting}
            className="hover:bg-accent flex items-center gap-1 rounded-md px-2 py-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2"
            aria-label={t("manageBilling", { ns: "billing" })}
          >
            {isRedirecting ? (
              <div className="border-muted-foreground h-4 w-4 animate-spin rounded-full border-b-2"></div>
            ) : (
              <Icon
                name="CreditCard"
                className="text-foreground h-4 w-4 flex-shrink-0"
              />
            )}
            <span className="text-foreground hidden text-sm font-normal sm:inline">
              {t("manageBilling", { ns: "billing" })}
            </span>
          </button>
        )}

        {/* Learn dropdown - only shown when current route is in LEARN_PAGE_MAP */}
        {!isWhiteLabel() && currentPageInfo && (
          <div className="relative" ref={learnDropdownRef}>
            <button
              type="button"
              onClick={() => setIsLearnDropdownOpen(!isLearnDropdownOpen)}
              className={`flex items-center gap-1 rounded-md px-2 py-1.5 transition-colors sm:gap-2 ${
                isLearnDropdownOpen ? "bg-accent" : "hover:bg-accent"
              }`}
              aria-expanded={isLearnDropdownOpen}
              aria-haspopup="true"
              aria-label={t("topbar.learn", { ns: "navigation" })}
            >
              <Icon
                name="BookOpen"
                className="text-foreground h-4 w-4 flex-shrink-0"
              />
              <span className="text-foreground hidden text-sm font-normal sm:inline">
                {t("topbar.learn", { ns: "navigation" })}
              </span>
              <Icon
                name="ChevronDown"
                className="text-muted-foreground h-4 w-4 flex-shrink-0"
              />
            </button>

            {isLearnDropdownOpen && (
              <div
                className="bg-popover border-border absolute start-0 top-full z-50 mt-1 w-56 max-w-[calc(100vw-2rem)] min-w-[200px] rounded-md border shadow-lg sm:start-auto sm:end-0 sm:max-w-none sm:min-w-0"
                role="menu"
                aria-label={t("topbar.learnMenu", { ns: "navigation" })}
              >
                <div className="py-2">
                  <a
                    href={`${SUPPORT_BASE_URL}${currentPageInfo.path}`.replace(
                      /\/+$/,
                      "",
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:bg-accent text-foreground flex w-full items-center gap-3 px-3 py-2 text-start text-sm transition-colors"
                    role="menuitem"
                    onClick={() => setIsLearnDropdownOpen(false)}
                  >
                    <Icon name="BookOpen" className="h-4 w-4 flex-shrink-0" />
                    <span>
                      {t("topbar.learnAboutPage", {
                        ns: "navigation",
                        page: t(currentPageInfo.labelKey, { ns: "navigation" }),
                      })}
                    </span>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        <LocaleSelector
          id="ui-locale-selector"
          label={t("language.label", { ns: "app" })}
          showLabel={false}
          showLeadingIcon={false}
          value={locale}
          onChange={handleLocaleChange}
          options={supportedLocales}
          containerClassName="hidden md:block"
          selectClassName="h-8 min-w-[108px] border-white/15 bg-slate-900/70 py-1 text-[13px]"
        />

        <div className="xs:block hidden">
          <button
            onClick={toggleNotifications}
            className={`flex items-center justify-center rounded-md p-1.5 transition-colors sm:px-2 sm:py-1.5 ${
              notificationsEnabled
                ? "bg-green-600 text-white hover:bg-green-700"
                : "bg-gray-800 text-white hover:bg-gray-700"
            }`}
            title={
              notificationsEnabled
                ? t("topbar.disableNotifications", { ns: "navigation" })
                : t("topbar.enableNotifications", { ns: "navigation" })
            }
          >
            <Icon
              name={notificationsEnabled ? "Bell" : "BellOff"}
              className="h-4 w-4 sm:h-4 sm:w-4"
            />
            <span className="ms-1 hidden text-xs lg:inline">
              {notificationsEnabled
                ? t("common.on", { ns: "app" })
                : t("common.off", { ns: "app" })}
            </span>
          </button>
        </div>

        {!hideThemeToggle && (
          <div className="xs:block hidden">
            <button
              onClick={handleThemeToggle}
              className="hover:bg-accent flex-shrink-0 rounded-md p-1.5 transition-colors"
              title={
                theme === "dark"
                  ? t("topbar.switchToLight", { ns: "navigation" })
                  : t("topbar.switchToDark", { ns: "navigation" })
              }
            >
              {theme === "dark" ? (
                <Icon
                  name="Sun"
                  className="h-4 w-4 text-yellow-400 sm:h-5 sm:w-5"
                />
              ) : (
                <Icon
                  name="Moon"
                  className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                />
              )}
            </button>
          </div>
        )}
        <div className="hidden sm:block">
          <LiveIndicator
            isConnected={isConnected}
            connectionState={connectionState}
            reconnectAttempt={reconnectAttempt}
            maxReconnectAttempts={maxReconnectAttempts}
          />
        </div>

        <div className="relative" ref={userDropdownRef}>
          <button
            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
            className={`flex items-center gap-1 rounded-md p-1 transition-colors sm:gap-2 sm:p-1.5 ${
              isUserDropdownOpen ? "bg-accent" : "hover:bg-accent"
            }`}
          >
            <div className="bg-brand-gradient flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full sm:h-8 sm:w-8">
              {user.photo ? (
                <img
                  src={user.photo}
                  alt={t("common.user", { ns: "app" })}
                  className="h-7 w-7 rounded-full object-cover sm:h-8 sm:w-8"
                />
              ) : (
                <Icon
                  name="User"
                  className="h-3.5 w-3.5 text-white sm:h-4 sm:w-4"
                />
              )}
            </div>
            <div className="hidden min-w-0 text-start md:block">
              <div className="text-foreground max-w-24 truncate text-sm font-normal lg:max-w-32">
                {user.name || user.email || t("common.user", { ns: "app" })}
              </div>
              <div className="text-muted-foreground max-w-24 truncate text-xs lg:max-w-32">
                {user.role || t("common.user", { ns: "app" })}
              </div>
            </div>
            <Icon
              name="ChevronDown"
              className="text-muted-foreground hidden h-3 w-3 flex-shrink-0 sm:block sm:h-4 sm:w-4"
            />
          </button>

          {/* User Dropdown */}
          {isUserDropdownOpen && (
            <div className="bg-popover border-border absolute end-0 top-full z-50 mt-1 w-56 rounded-md border shadow-lg">
              <div className="py-2">
                <div className="border-border border-b px-3 py-2">
                  <div className="text-foreground truncate font-normal">
                    {user.name || t("common.user", { ns: "app" })}
                  </div>
                  <div className="text-muted-foreground truncate text-sm">
                    {user.email}
                  </div>
                </div>

                <div className="border-border border-b px-3 py-2 md:hidden">
                  <LocaleSelector
                    id="ui-locale-selector-mobile"
                    label={t("language.label", { ns: "app" })}
                    showLabel={false}
                    showLeadingIcon={false}
                    value={locale}
                    onChange={handleLocaleChange}
                    options={supportedLocales}
                    containerClassName="w-full"
                    selectClassName="h-8 min-w-0 w-full border-white/15 bg-slate-900/70 py-1 text-[13px]"
                  />
                </div>

                <button
                  onClick={handleMyAccountClick}
                  className="hover:bg-accent text-muted-foreground hover:text-foreground w-full cursor-pointer px-3 py-2 text-start transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icon name="User" className="h-4 w-4" />
                    <span>{t("topbar.account", { ns: "navigation" })}</span>
                  </div>
                </button>

                <div className="border-border my-1 border-t"></div>

                <button
                  onClick={handleLogout}
                  disabled={logoutMutation.isPending}
                  className="hover:bg-error-bg text-error hover:text-error disabled:hover:text-error w-full cursor-pointer px-3 py-2 text-start transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  <div className="flex items-center gap-3">
                    {logoutMutation.isPending ? (
                      <div className="border-error h-4 w-4 animate-spin rounded-full border-b-2"></div>
                    ) : (
                      <Icon name="LogOut" className="h-4 w-4" />
                    )}
                    <span>
                      {logoutMutation.isPending
                        ? t("topbar.loggingOut", { ns: "navigation" })
                        : t("topbar.logout", { ns: "navigation" })}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
